import React, { useState, useEffect } from "react";
import {
  Flex,
  Card,
  Button,
  Divider,
  Text,
  TabGroup,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Title,
} from "@tremor/react";
import ThemedCodeEditor from "../../components/ThemedCodeEditor";
import rehypePrism from "@mapbox/rehype-prism";
import * as profilesAPI from "../../services/profiles";

import DecoderHelp from "./help";
import TestPanel from "./test-panel";
import { generateFromSchema } from "./templates";
import { decoderLabel } from "./type-labels";
import useCatalogEntries from "./useCatalogEntries";

function TestPill({ state }) {
  const map = {
    untested:  { label: "Untested", cls: "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300", title: "Run the Test tab to validate this decoder" },
    passed:    { label: "✓ Tested", cls: "bg-green-100 dark:bg-green-950/40 text-green-800 dark:text-green-300", title: "Last test passed for the current code" },
    failed:    { label: "✗ Last run failed", cls: "bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300", title: "Last test failed — see the Test tab" },
    no_schema: { label: "No schema declared", cls: "bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300", title: "Decoder ran without errors, but no fields are declared. Add fields in the Schema tab to enable contract validation." },
  };
  const s = map[state] || map.untested;
  return (
    <span
      title={s.title}
      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

/**
 * Decoder component for the Profile page.
 *
 * The decoder type is owned by the Schema tab — this component just reads
 * `profile.decoder_type` and renders the appropriate surface:
 *   - Custom: TabGroup with Editor / Test / Help (full authoring + testing).
 *   - Built-in (CayenneLPP and the device catalog): inline TestPanel with a one-line note.
 */
export default function Decoder({ profile }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastTestResult, setLastTestResult] = useState(null);
  const { entries: catalog } = useCatalogEntries();

  const decoderType = profile.decoder_type || "cayennelpp";
  const isCustom = decoderType === "custom";

  useEffect(() => {
    setCode(profile.decoder || "");
  }, [profile]);

  const onUpdateCode = async () => {
    setLoading(true);
    await profilesAPI.updateCode(profile._id, code);
    setLastTestResult(null);
    setLoading(false);
  };

  const onGenerateFromSchema = () => {
    if (!profile.capabilities?.length) return;
    if (code && code.trim().length > 0) {
      const ok = window.confirm(
        "This will replace your current decoder code. Continue?"
      );
      if (!ok) return;
    }
    setCode(generateFromSchema(profile.capabilities));
  };

  // Pill state derives from comparing current editor text to the code that was
  // tested, on every render. No effects, no debounce — pure derivation.
  const pillState = (() => {
    if (!lastTestResult) return "untested";
    if (code !== lastTestResult.testedCode) return "untested";
    if (lastTestResult.status === "pass") return "passed";
    if (lastTestResult.status === "no_schema") return "no_schema";
    return "failed";
  })();

  if (!isCustom) {
    return (
      <Card>
        <Text className="mb-4">
          This profile uses the built-in <b>{decoderLabel(decoderType, catalog)}</b> decoder.
          Use Test to sanity-check a real payload.
        </Text>
        <TestPanel profile={profile} />
      </Card>
    );
  }

  return (
    <Card>
      <Text className="mb-2">
        Decoder function is used to decode incoming device payload into a
        normalized structure. In the case of LoRaWAN, here hex/base64 strings
        are decoded to Zafron's JSON Schema.
      </Text>
      <TabGroup>
        <TabList variant="solid" defaultValue="1">
          <Tab value="1">Editor</Tab>
          <Tab value="2">Test</Tab>
          <Tab value="3">Help</Tab>
        </TabList>
        <Divider className="mb-3" />
        <TabPanels>
          <TabPanel>
            <div className="mb-2">
              <Flex justifyContent="between">
                <Title>Editor</Title>
                <Flex justifyContent="end" className="gap-2 w-auto">
                  <span
                    title={
                      profile.capabilities?.length
                        ? ""
                        : "Add fields in the Schema tab first"
                    }
                  >
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={onGenerateFromSchema}
                      disabled={!profile.capabilities?.length}
                    >
                      Generate decoder from schema
                    </Button>
                  </span>
                  <TestPill state={pillState} />
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={onUpdateCode}
                    loading={loading}
                  >
                    Update
                  </Button>
                </Flex>
              </Flex>
            </div>
            <ThemedCodeEditor
              value={code}
              language="js"
              placeholder="Please enter JS code."
              onChange={(evn) => setCode(evn.target.value)}
              padding={15}
              rehypePlugins={[[rehypePrism, { ignoreMissing: true }]]}
              style={{
                overflow: "auto",
                borderRadius: 5,
                border: "1px solid #e1e1e1",
                fontFamily:
                  "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
              }}
            />
          </TabPanel>
          <TabPanel>
            <TestPanel profile={profile} onTestResult={setLastTestResult} />
          </TabPanel>
          <TabPanel>
            <DecoderHelp />
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </Card>
  );
}
